import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Field, Button, Chip } from "@/components/ui";
import Seo from "@/components/Seo";
import { ImmersiveShell } from "@/components/immersive/ImmersiveShell";
import { Reveal } from "@/components/immersive/Reveal";
import Breadcrumbs from "@/components/immersive/Breadcrumbs";
import { TrustSignals } from "@/components/immersive/MarketingBits";
import { api } from "@/lib/api";
import { track } from "@/lib/track";

const emailValid = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const CARE_TYPES = [
  "Personal Care", "Companion Care", "Senior Care", "Dementia & Alzheimer's Support",
  "Respite Care", "Overnight / Live-In Care", "I'm not sure what type of care I need",
];

export default function BookAppointment() {
  const nav = useNavigate();
  const [d, setD] = useState({ name: "", phone: "", email: "", zip_code: "", care_type: "", start_date: "", schedule: "", notes: "" });
  const [started, setStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    if (!started) { setStarted(true); track("form_started", { form: "book_appointment" }); }
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
    setD((s) => ({ ...s, [k]: v }));
  };

  const submit = async () => {
    if (busy) return;
    const errs = {};
    if (!d.name.trim()) errs.name = "Please enter your name.";
    if (!d.phone.trim()) errs.phone = "Please enter a phone number we can reach you at.";
    if (d.email && !emailValid(d.email)) errs.email = "Please enter a valid email address, or leave it blank.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setBusy(true); setErrors({});
    try {
      const payload = { ...d, source: "book_appointment" };
      if (!payload.email) delete payload.email;
      await api.post("/leads", payload, false);
      track("form_submitted", { form: "book_appointment" });
      track("appointment_booked", { care_type: d.care_type || "unspecified" });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErrors({ form: e?.message || "Something went wrong. Please try again." });
    } finally { setBusy(false); }
  };

  return (
    <ImmersiveShell>
      <Seo title="Book an Appointment" path="/book-appointment"
        description="Book an appointment with a HomecareMatches representative. Tell us your care needs and we help you find home care services and a potential caregiver match through partner agencies."
        keywords="book a home care services appointment, senior care, in-home care, caregiver matching" />
      <section className="imm-mkt-hero imm-wrap" data-testid="book-appointment" style={{ minHeight: "auto", paddingTop: 20 }}>
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Book an Appointment" }]} />
        <Reveal className="imm-eyebrow" delay={80}>Guidance</Reveal>
        <Reveal as="h1" delay={180}>Book an Appointment</Reveal>
        <Reveal as="p" delay={300}>Schedule a conversation with a HomecareMatches representative. We will help you understand your options for home care services and determine the next step. Not sure what type of care you need? That is exactly what this is for.</Reveal>
      </section>

      <section className="imm-wrap" data-testid="book-form-section" style={{ paddingBottom: "8vh" }}>
        {done ? (
          <div className="imm-contact-done" data-testid="book-success" role="status" aria-live="polite">
            <Icon name="check-circle" size={44} color="var(--success)" />
            <h2>Thank You — We Received Your Request</h2>
            <p>A HomecareMatches representative will review your information and help guide you toward the next step.</p>
            <div className="imm-cta-row" style={{ justifyContent: "center" }}>
              <Button testID="book-get-matched" icon="heart" label="Get Matched Now" onClick={() => { track("get_matched_clicked", { from: "book_success" }); nav("/intake"); }} />
              <Button testID="book-home" variant="secondary" label="Back to home" onClick={() => nav("/")} />
            </div>
          </div>
        ) : (
          <div className="imm-contact-form" style={{ maxWidth: 620 }}>
            <Field testID="book-name" label="Your name" required error={errors.name} value={d.name} onChange={(v) => set("name", v)} placeholder="Full name" />
            <Field testID="book-phone" label="Phone" required error={errors.phone} value={d.phone} onChange={(v) => set("phone", v)} placeholder="(201) 555-0100" inputMode="tel" />
            <Field testID="book-email" label="Email (optional)" error={errors.email} value={d.email} onChange={(v) => set("email", v)} placeholder="you@example.com" inputMode="email" autoCapitalize="none" />
            <Field testID="book-zip" label="ZIP code" value={d.zip_code} onChange={(v) => set("zip_code", v)} placeholder="07030" inputMode="numeric" />
            <div role="group" aria-label="Type of care needed" style={{ marginTop: 6 }}>
              <div className="field-label" id="book-care-label">Type of care needed</div>
              <div className="chip-wrap">
                {CARE_TYPES.map((c) => <Chip key={c} testID={`book-care-${c.slice(0, 6).replace(/\W/g, "").toLowerCase()}`} label={c} selected={d.care_type === c} onClick={() => set("care_type", d.care_type === c ? "" : c)} />)}
              </div>
            </div>
            <Field testID="book-start" label="Preferred start date" value={d.start_date} onChange={(v) => set("start_date", v)} placeholder="e.g. As soon as possible, or 2026-07-01" />
            <Field testID="book-schedule" label="Preferred schedule" value={d.schedule} onChange={(v) => set("schedule", v)} placeholder="e.g. Weekday mornings, about 20 hrs/week" />
            <Field testID="book-notes" label="Additional needs or preferences" value={d.notes} onChange={(v) => set("notes", v)} placeholder="Anything else that will help us understand your situation…" multiline maxLength={5000} />
            <div aria-live="assertive" role="alert" data-testid="book-error">
              {errors.form ? <p className="err-text">{errors.form}</p> : Object.keys(errors).some((k) => k !== "form" && errors[k]) ? <p className="err-text">Please fix the highlighted fields above.</p> : null}
            </div>
            <Button testID="book-submit" icon="calendar" label={busy ? "Sending…" : "Request my appointment"} onClick={submit} loading={busy} />
            <div style={{ marginTop: 16 }}><TrustSignals /></div>
            <p className="note" style={{ marginTop: 10 }}>Prefer to browse first? See <a href="/how-it-works">how it works</a> or <a href="/intake">get matched now</a>.</p>
          </div>
        )}
      </section>
    </ImmersiveShell>
  );
}
